import { Injectable } from "@angular/core";
import { HttpClient, HttpParams } from "@angular/common/http";
import { API_BASE_URL } from "./api-base";
import {
  DiscussionPost,
  LearningPath,
  ProblemCategory,
  ProblemDetail,
  ProblemEditorial,
  ProblemHintStep,
  ProblemLibraryResponse,
  ProblemListItem,
  WorldMap
} from "../models/problem.models";

@Injectable({ providedIn: "root" })
export class ProblemService {
  constructor(private readonly http: HttpClient) {}

  getWorldMap() {
    return this.http.get<WorldMap[]>(`${API_BASE_URL}/problems/worlds`);
  }

  getProblem(problemId: number) {
    return this.http.get<ProblemDetail>(`${API_BASE_URL}/problems/${problemId}`);
  }

  getHint(problemId: number) {
    return this.http.get<ProblemHintStep>(`${API_BASE_URL}/problems/${problemId}/hint`);
  }

  getHintStep(problemId: number, step: number) {
    return this.http.get<ProblemHintStep>(`${API_BASE_URL}/problems/${problemId}/hints/${step}`);
  }

  getEditorial(problemId: number) {
    return this.http.get<ProblemEditorial>(`${API_BASE_URL}/problems/${problemId}/editorial`);
  }

  getProblemLibrary(query?: {
    difficulty?: string;
    topic?: string;
    solved?: "solved" | "unsolved" | "all";
    search?: string;
    sort?: "recommended" | "popular" | "newest";
    page?: number;
    pageSize?: number;
  }) {
    let params = new HttpParams();
    if (query?.difficulty && query.difficulty !== "All") {
      params = params.set("difficulty", query.difficulty);
    }
    if (query?.topic && query.topic !== "All") {
      params = params.set("topic", query.topic);
    }
    if (query?.solved && query.solved !== "all") {
      params = params.set("solved", query.solved);
    }
    if (query?.search && query.search.trim().length > 0) {
      params = params.set("search", query.search.trim());
    }
    if (query?.sort) {
      params = params.set("sort", query.sort);
    }
    if (query?.page) {
      params = params.set("page", query.page);
    }
    if (query?.pageSize) {
      params = params.set("pageSize", query.pageSize);
    }
    return this.http.get<ProblemLibraryResponse>(`${API_BASE_URL}/problems`, { params });
  }

  getCategories() {
    return this.http.get<ProblemCategory[]>(`${API_BASE_URL}/problems/categories`);
  }

  getLearningPaths() {
    return this.http.get<LearningPath[]>(`${API_BASE_URL}/problems/paths`);
  }

  getDiscussion(problemId: number) {
    return this.http.get<DiscussionPost[]>(`${API_BASE_URL}/community/problem/${problemId}`);
  }

  postDiscussion(problemId: number, content: string, parentId?: number | null) {
    const payload: { content: string; parentId?: number } = { content };
    if (parentId) {
      payload.parentId = parentId;
    }
    return this.http.post<DiscussionPost>(`${API_BASE_URL}/community/problem/${problemId}/posts`, payload);
  }

  upvoteDiscussion(postId: number) {
    return this.http.post<{ postId: number; upvotes: number; hasUpvoted: boolean }>(
      `${API_BASE_URL}/community/posts/${postId}/upvote`,
      {}
    );
  }
}
